import React, { createContext, useContext, useEffect, useRef, useState, } from "react";
import Cookies from "js-cookie";
import { fetchTopics, deleteTopic, fetchChatHistory } from "../services/topicApi";
import { refreshToken } from "../services/loginApi";
import { jwtDecode } from "jwt-decode";
import { ConfigContext } from "../utils/Config.jsx";
import { ApiContext, getEndpoints } from "../utils/ApiContext";
import WebSocketService from "../utils/websocketService";
import {
  fetchUserCollections,
  verifyAdminCollectionAccess
} from "./adminUtils";

export const ContextApp = createContext();

let webSocketServiceInstance = null;

const AppContext = ({ children }) => {
  const configContext = useContext(ConfigContext);
  const config = configContext?.config || {};
  const isLoadingConfig = configContext?.isLoading || false;
  const apiContext = useContext(ApiContext);
  const apiEndpoints = apiContext?.endpoints || {};
  const isLoadingApi = apiContext?.isLoading || false;
  const [showSlide, setShowSlide] = useState(false);
  const [chatValue, setChatValue] = useState("");
  const [message, setMessage] = useState(() => {
    const storedMessages = localStorage.getItem("chatHistory");
    return storedMessages ? JSON.parse(storedMessages) : [];
  });

  const [hasAccessError, setHasAccessError] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isAwaitingNewResponse, setIsAwaitingNewResponse] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [topics, setTopics] = useState([]);
  const [isInitialChat, setIsInitialChat] = useState(true);
  const [reloadTopicsTrigger, setReloadTopicsTrigger] = useState(0);
  const [currentCollectionDocuments, setCurrentCollectionDocuments] = useState(
    []
  );
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [isCollectionSelectorOpen, setIsCollectionSelectorOpen] =
    useState(false);

  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isChatOpened, setIsChatOpened] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => !!Cookies.get("accessToken")
  );
  const [isGuest, setIsGuest] = useState(() => !Cookies.get("accessToken"));
  const [expirationDate, setExpirationDate] = useState(() => {
    const expiration = Cookies.get("expirationDate");
    return expiration ? new Date(expiration) : null;
  });
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState(() => {
    const fromCookies = Cookies.get("username");
    if (fromCookies) return fromCookies;
    const fromLocalStorage = localStorage.getItem("username");
    if (fromLocalStorage) return fromLocalStorage;

    try {
      const accessToken = Cookies.get("accessToken");
      if (accessToken) {
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken && decodedToken.username) {
          return decodedToken.username;
        }
      }
    } catch (error) {
      console.error("Error al decodificar token para username:", error);
    }

    return null;
  });

  const setShouldLoadHistory = useState(false);
  const awaitingResponseRef = useRef(isAwaitingNewResponse);
  const messageHandlerRef = useRef(null);
  const errorHandlerRef = useRef(null);
  const closeHandlerRef = useRef(null);

  useEffect(() => {
    // Logic for decoding the token
    const accessToken = Cookies.get("accessToken");
    let decodedUsername = null;
    let decodedUserRole = null;

    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        decodedUsername = decoded.username;
        const isAdmin = (decoded &&
          ((decoded["cognito:groups"] &&
            Array.isArray(decoded["cognito:groups"]) &&
            decoded["cognito:groups"].includes("admin")) ||
            decoded.username === "admin"));
        decodedUserRole = isAdmin ? "admin" : "user";
      } catch (error) {
        console.error("Error al decodificar el token:", error);
        // Optionally clear state on error
        setLastError("Error al autenticar. Por favor, inicia sesión de nuevo.");
        setHasAccessError(true);
      }
    }

    if (userRole !== decodedUserRole) {
      setUserRole(decodedUserRole);
    }
    if (username !== decodedUsername) {
      setUsername(decodedUsername);
    }

    // Guest user logic
    if (isGuest) {
      if (config && config.DEFAULT_COLLECTION) {
        const guestCollection = [config.DEFAULT_COLLECTION];

        if (JSON.stringify(collections) !== JSON.stringify(guestCollection)) {
          setCollections(guestCollection);
        }

        if (selectedCollection !== config.DEFAULT_COLLECTION) {
          setSelectedCollection(config.DEFAULT_COLLECTION);
        }

        if (webSocketServiceInstance) {
          webSocketServiceInstance.updateContext(config.DEFAULT_COLLECTION, "");
        }
      } else {
        if (collections.length > 0) {
          setCollections([]);
        }
        if (selectedCollection !== "") {
          setSelectedCollection("");
        }
      }
      return;
    }

    // Logged-in user logic
    // if (isLoggedIn && !hasAccessError) {
    //   loadCollections();
    // }

  }, [config, isGuest, userRole, username]);

  useEffect(() => {
    if (message && message.length > 0) {
      localStorage.setItem("chatHistory", JSON.stringify(message));
    }
  }, [message]);

  useEffect(() => {
    if (webSocketServiceInstance) {
      const connectionStatusListener = (isConnected) => {
      };

      webSocketServiceInstance.addConnectionStatusListener(connectionStatusListener);

      return () => {
        if (webSocketServiceInstance) {
          webSocketServiceInstance.removeConnectionStatusListener(connectionStatusListener);
        }
      };
    }
  }, [webSocketServiceInstance]);

  useEffect(() => {
    if (currentTopic && currentTopic.id) {
      if (message.length === 0 && !isInitialChat) {
        loadChatHistoryForTopic(currentTopic.id).catch(error => {
          console.error("Error al cargar historial:", error);
        });
      }
    }
  }, [currentTopic]);


  useEffect(() => {
    if (isLoggedIn && !prevLoginState.current) {
      setCurrentTopic(null);
      setMessage([]);
      setIsInitialChat(true);

      if (webSocketServiceInstance) {
        webSocketServiceInstance.close();
        webSocketServiceInstance.currentTopicId = null;
        webSocketServiceInstance.initialMessageSent = false;
        webSocketServiceInstance.isInitialConnection = true;

        setTimeout(() => {
          webSocketServiceInstance.forceReconnect();
        }, 800);
      }
    }

    prevLoginState.current = isLoggedIn;
  }, [isLoggedIn]);


  const prevLoginState = useRef(false);
  useEffect(() => {
    sessionStorage.removeItem('currentTopic');
    localStorage.removeItem('currentTopic');
    setCurrentTopic(null);
    setMessage([]);
    setIsInitialChat(true);

    if (!webSocketServiceInstance) {
      webSocketServiceInstance = WebSocketService.getInstance(getEndpoints);
    }

    messageHandlerRef.current = handleWebSocketMessage;
    errorHandlerRef.current = (errorMessage) => { /* ... */ };
    closeHandlerRef.current = (event) => { /* ... */ };

    if (webSocketServiceInstance) {
      webSocketServiceInstance.addMessageListener(messageHandlerRef.current);
      webSocketServiceInstance.addErrorListener(errorHandlerRef.current);
      webSocketServiceInstance.addCloseListener(closeHandlerRef.current);
    }

    return () => {
      if (webSocketServiceInstance) {
        if (messageHandlerRef.current) webSocketServiceInstance.removeMessageListener(messageHandlerRef.current);
        if (errorHandlerRef.current) webSocketServiceInstance.removeErrorListener(errorHandlerRef.current);
        if (closeHandlerRef.current) webSocketServiceInstance.removeCloseListener(closeHandlerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    awaitingResponseRef.current = isAwaitingNewResponse;
  }, [isAwaitingNewResponse]);

  useEffect(() => {
    const handleLoginStateChange = async () => {
      if (isLoggedIn !== prevLoginState.current) {
        if (isLoggedIn) {
          setCurrentTopic(null);
          setMessage([]);
          setIsInitialChat(true);
          setIsWaitingForResponse(false);
          setIsAwaitingNewResponse(false);

          if (webSocketServiceInstance) {
            webSocketServiceInstance.close();
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          const newWebSocketInstance = WebSocketService.getInstance(getEndpoints, true);
          webSocketServiceInstance = newWebSocketInstance;

          if (messageHandlerRef.current) {
            webSocketServiceInstance.addMessageListener(messageHandlerRef.current);
          }
          if (errorHandlerRef.current) {
            webSocketServiceInstance.addErrorListener(errorHandlerRef.current);
          }
          if (closeHandlerRef.current) {
            webSocketServiceInstance.addCloseListener(closeHandlerRef.current);
          }

          webSocketServiceInstance.resetForLogin();
          await new Promise(resolve => setTimeout(resolve, 800));
          if (selectedCollection) {
            webSocketServiceInstance.updateContext(selectedCollection, "");
          }
          try {
            await webSocketServiceInstance.forceReconnect();
          } catch (err) {
            console.error("Error al reconectar WebSocket después del login:", err);
          }
        }
      }
      prevLoginState.current = isLoggedIn;
    };

    handleLoginStateChange();
  }, [isLoggedIn, selectedCollection]);

  useEffect(() => {
    if (username) {
      Cookies.set("username", username);
      localStorage.setItem("username", username);
    } else {
      Cookies.remove("username");
      localStorage.removeItem("username");
    }
  }, [username]);
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('currentTopic');
      localStorage.removeItem('currentTopic');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!hasAccessError) {
      loadTopics();
      resetChat();
    }
  }, [reloadTopicsTrigger, isLoggedIn, isGuest, hasAccessError]);

  const triggerLoadHistory = (load) => {
    setShouldLoadHistory(load);
  };

  const loadTopics = async () => {
    if (isGuest || hasAccessError) {
      setTopics([]);
      return;
    }
    try {
      let fetchedTopics = await fetchTopics();
      setTopics(fetchedTopics);
    } catch (error) {
      console.error("Error al cargar los tópicos:", error);
      setTopics([]);
    }
  };

  const loadCollections = async () => {
    if (hasAccessError) {
      return [];
    }

    if (isGuest) {
      if (config && config.DEFAULT_COLLECTION) {
        const guestCollection = [config.DEFAULT_COLLECTION];
        setCollections(guestCollection);
        setSelectedCollection(config.DEFAULT_COLLECTION);
        if (webSocketServiceInstance) {
          webSocketServiceInstance.updateContext(
            config.DEFAULT_COLLECTION,
            currentTopic ? currentTopic.id : ""
          );
        }

        return guestCollection;
      } else {
        return [];
      }
    }

    try {
      const result = await fetchUserCollections();

      setCollections(result.collections);

      const newSelectedCollection =
        (selectedCollection && result.collections.includes(selectedCollection))
          ? selectedCollection
          : result.collections[0];

      setSelectedCollection(newSelectedCollection);
      localStorage.setItem("selectedCollection", newSelectedCollection);

      if (webSocketServiceInstance) {
        webSocketServiceInstance.updateContext(
          newSelectedCollection,
          currentTopic ? currentTopic.id : ""
        );
      }

      setLastError(null);
      setHasAccessError(false);
      return result.collections;

    } catch (error) {
      console.error("Error al cargar las colecciones:", error);
      const errorMessage = error.message.includes("no tiene colecciones configuradas")
        ? "El usuario no tiene un perfil asociado o no tiene acceso a colecciones. Contacte al administrador del sistema."
        : "No tienes acceso a ninguna colección. Contacta con el administrador.";

      setLastError(errorMessage);
      setHasAccessError(true);
      setCollections([]);
      setSelectedCollection("");
      localStorage.removeItem("selectedCollection");
      return [];
    }
  };
  const handleWebSocketMessage = (data) => {

    if (data.error) {
      console.error("Error del WebSocket:", data.error);
      setIsWaitingForResponse(false);
      setIsAwaitingNewResponse(false);

      const errorMessage = isGuest
        ? "No se pudo procesar tu consulta. Por favor, intenta con otra pregunta."
        : "Error al procesar tu consulta. Por favor, intenta nuevamente.";

      setMessage((prevMessages) => [
        ...prevMessages,
        {
          text: errorMessage,
          isBot: true,
          isError: true,
        },
      ]);
      return;
    }

    if (data.TOPIC_ID) {
      setCurrentTopic({
        id: data.TOPIC_ID,
        name: data.TOPIC_NAME,
      });
      loadTopics();
    } else if (data.type === "topics_refresh") {
      setIsWaitingForResponse(false);
      loadTopics();
    } else if (Array.isArray(data)) {
      if (awaitingResponseRef.current) {
        setIsAwaitingNewResponse(false);
      }
      setMessage((prevMessages) => {
        const lastMessage =
          prevMessages.length > 0
            ? prevMessages[prevMessages.length - 1]
            : null;
        if (lastMessage && lastMessage.isBot) {
          const combinedText = data.map((item) => item.text).join("");
          return [
            ...prevMessages.slice(0, prevMessages.length - 1),
            { ...lastMessage, text: lastMessage.text + combinedText },
          ];
        } else {
          const combinedText = data.map((item) => item.text).join("");
          return [...prevMessages, { text: combinedText, isBot: true }];
        }
      });
    }

    setTimeout(() => {
      setIsWaitingForResponse(false);
      setIsAwaitingNewResponse(false);
    }, 1500);
  };
  const loadDocumentsForCollection = async (collectionName, resetChatOnChange = true) => {
    if (hasAccessError) {
      return false;
    }

    if (isGuest) {
      if (webSocketServiceInstance) {
        if (!resetChatOnChange) {
          webSocketServiceInstance.updateCollectionWithoutReset(collectionName, currentTopic ? currentTopic.id : "");
        } else {
          webSocketServiceInstance.updateContext(collectionName, "");
        }

        if (webSocketServiceInstance.ws && webSocketServiceInstance.ws.readyState === WebSocket.OPEN) {
          const guestToken = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJHVUVTVF9VU0VSIiwiY29nbml0bzpncm91cHMiOlsiR1VFU1RfR1JPVVAiXSwiaXNzIjoiZnJvbnQiLCJ0b2tlbl91c2UiOiJhY2Nlc3MiLCJ1c2VybmFtZSI6IkdVRVNUX1VTRVIifQ.PcqpR_wHkLVs1_hnnThLqPfyq9huDLAEPgOojKSVXJo";

          const messageData = {
            collection_change: true,
            collection: collectionName,
            Authorization: `Bearer ${guestToken}`,
            reset_context: resetChatOnChange,
            exclusive_collection: true,
            maintain_conversation: !resetChatOnChange
          };

          webSocketServiceInstance.ws.send(JSON.stringify(messageData));
        }
      }
      return true;
    }

    if (!collectionName) {
      console.error("Nombre de colección vacío");
      setLastError(
        "No tienes acceso a ninguna colección. Contacta con el administrador."
      );
      setHasAccessError(true);
      return false;
    }

    try {
      const accessToken = Cookies.get("accessToken");
      const decodedToken = jwtDecode(accessToken);
      const currentUsername = decodedToken.username;

      if (resetChatOnChange) {
        setCurrentTopic(null);
        setMessage([]);
        setIsInitialChat(true);
      }

      try {
        const endpoints = await getEndpoints();
        verifyAdminCollectionAccess(collectionName, currentUsername, apiEndpoints, endpoints);

        if (webSocketServiceInstance) {
          if (!resetChatOnChange) {
            webSocketServiceInstance.updateCollectionWithoutReset(
              collectionName,
              currentTopic ? currentTopic.id : ""
            );
          } else {
            webSocketServiceInstance.updateContext(
              collectionName,
              ""
            );
          }

          if (webSocketServiceInstance.ws && webSocketServiceInstance.ws.readyState === WebSocket.OPEN) {
            const messageData = {
              collection_change: true,
              collection: collectionName,
              Authorization: `Bearer ${accessToken}`,
              reset_context: resetChatOnChange,
              exclusive_collection: true,
              maintain_conversation: !resetChatOnChange
            };

            webSocketServiceInstance.ws.send(JSON.stringify(messageData));
          }
        }

        setLastError(null);
        return true;
      } catch (error) {
        console.error("Error al verificar acceso del administrador:", error);
        setLastError(`No tienes acceso a la colección: ${collectionName}`);
        setHasAccessError(true);
        return false;
      }
    } catch (error) {
      console.error("Error al cargar los documentos de la colección:", error);
      setLastError(
        "No tienes acceso a ninguna colección. Contacta con el administrador."
      );
      setHasAccessError(true);
      return false;
    }
  };

  const updateCollectionContext = async (collection, topicId = "") => {
    if (!collection) return false;

    if (hasAccessError) return false;

    try {
      if (webSocketServiceInstance) {
        webSocketServiceInstance.updateCollectionWithoutReset(collection, topicId);

        const hasAccess = await verifyCollectionAccess(collection);
        if (!hasAccess) {
          setLastError(`No tienes acceso a la colección: ${collection}`);
          return false;
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error al actualizar el contexto de colección:", error);
      return false;
    }
  };

  const verifyCollectionAccess = async (collectionName) => {
    if (isGuest || hasAccessError) return true;

    try {
      const accessToken = Cookies.get("accessToken");
      const decodedToken = jwtDecode(accessToken);
      const currentUsername = decodedToken.username;

      return true;
    } catch (error) {
      console.error("Error al verificar acceso a la colección:", error);
      return false;
    }
  };

  const resetWebSocketConnection = () => {
    if (webSocketServiceInstance) {
      webSocketServiceInstance.close();
      webSocketServiceInstance.connect().catch((err) => {
        console.error("Error al reconectar WebSocket:", err);
        setMessage((prevMessages) => [
          ...prevMessages,
          {
            text: "Error de conexión. Por favor, recarga la página.",
            isBot: true,
            isError: true,
          },
        ]);
      });
    }
    setIsWaitingForResponse(false);
    setIsAwaitingNewResponse(false);
  };
  const cleanupAfterLogout = () => {

    if (webSocketServiceInstance) {
      webSocketServiceInstance.close();
      webSocketServiceInstance.currentTopicId = null;
      webSocketServiceInstance.currentCollection = null;
      webSocketServiceInstance.initialMessageSent = false;
      webSocketServiceInstance.isInitialConnection = true;
      webSocketServiceInstance.messageQueue = [];
    }

    setCurrentTopic(null);
    setMessage([]);
    setIsWaitingForResponse(false);
    setIsAwaitingNewResponse(false);
    setLastError(null);
    setHasAccessError(false);
    setIsInitialChat(true);

    sessionStorage.removeItem('currentTopic');
    localStorage.removeItem('currentTopic');
    localStorage.removeItem('chatHistory');

  };

  const handleSend = async (inputText = chatValue.trim()) => {
    if (isInitialChat) {
      setIsInitialChat(false);
      if (!currentTopic) {

      } else {
      }
    }

    if (hasAccessError) {
      setLastError(
        "No tienes acceso a ninguna colección. Contacta con el administrador."
      );
      return;
    }

    if (!inputText) return;

    if (!isGuest) {
      try {
        await fetchUserCollections();
      } catch (error) {
        console.error("Error al verificar asociación antes de consulta:", error);
        setMessage((prevMessages) => [
          ...prevMessages,
          { text: inputText, isBot: false },
          {
            text: error.message || "Error al verificar tu acceso. Por favor, intenta nuevamente.",
            isBot: true,
            isError: true,
          },
        ]);
        setHasAccessError(true);
        return;
      }
    }

    setMessage((prevMessages) => [
      ...prevMessages,
      { text: inputText, isBot: false },
    ]);
    setChatValue("");

    setIsWaitingForResponse(true);
    setIsAwaitingNewResponse(true);

    try {
      if (webSocketServiceInstance) {
        const isNewConversation = !currentTopic;

        if (isNewConversation) {
          const possibleTopicName = inputText.length > 30 ?
            inputText.substring(0, 30) + "..." :
            inputText;
        }

        webSocketServiceInstance.sendChatMessage(
          inputText,
          config,
          currentTopic
        );
      } else {
        console.error("WebSocketService no está disponible");
        throw new Error("WebSocketService no disponible");
      }
    } catch (error) {
      console.error("Error al enviar el mensaje:", error);

      setTimeout(() => {
        setMessage((prevMessages) => [
          ...prevMessages,
          {
            text: "Error al enviar el mensaje. Por favor, intenta nuevamente.",
            isBot: true,
            isError: true,
          },
        ]);
        setIsWaitingForResponse(false);
        setIsAwaitingNewResponse(false);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        e.preventDefault();
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const loadChatHistoryForTopic = async (topicId) => {
    if (hasAccessError) {
      return;
    }

    setIsLoadingChat(true);
    try {
      if (webSocketServiceInstance) {
        if (selectedCollection) {
          webSocketServiceInstance.updateContext(selectedCollection, topicId);

          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      let history = await fetchChatHistory(topicId);

      let messagesArray = [];
      if (history && Array.isArray(history.messages)) {
        messagesArray = history.messages;
      } else if (Array.isArray(history)) {
        messagesArray = history;
      } else {
        console.warn("Formato de historial inesperado:", history);
        messagesArray = [];
      }

      setMessage(
        messagesArray.map((msg, index) => {
          const botIndicators = {
            type: ["ai", "bot", "assistant", "AIMessageChunk", "AIMessage"],
            role: ["assistant", "ai", "bot"],
            sender: ["bot", "ai", "assistant"],
            isBot: [true],
            is_bot: [true]
          };

          const userIndicators = {
            type: ["user", "human"],
            role: ["user"],
            sender: ["user"]
          };

          const isBot =
            Object.entries(botIndicators).some(([key, values]) =>
              values.includes(msg[key])
            ) ||
            (Object.entries(userIndicators).every(([key, values]) =>
              !values.includes(msg[key])
            ) &&
              Object.keys(msg).every(key =>
                !["type", "role", "sender", "isBot", "is_bot"].includes(key)
              ) &&
              index % 2 === 1);

          return {
            text: msg.content || msg.text || msg.message || "",
            isBot: isBot,
          };
        })
      );

      setCurrentTopic({ id: topicId });
      setIsInitialChat(false);
      setLastError(null);

      if (webSocketServiceInstance) {
        webSocketServiceInstance.loadChatHistoryForTopic(topicId);

        webSocketServiceInstance.currentCollection = selectedCollection;
        webSocketServiceInstance.currentTopicId = topicId;
      }
    } catch (error) {
      console.error("Error al cargar el historial del chat:", error);
      setCurrentTopic(null);
      setMessage([
        {
          text: "Error al cargar el historial del chat. Por favor, intenta nuevamente.",
          isBot: true,
          isError: true,
        },
      ]);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const resetChat = () => {
    setCurrentTopic(null);
    setMessage([]);
    setIsWaitingForResponse(false);
    setIsAwaitingNewResponse(false);
    setLastError(null);
    setHasAccessError(false);
    setIsInitialChat(true);

    if (webSocketServiceInstance) {
      try {
        webSocketServiceInstance.resetAllState();
        if (selectedCollection) {
          webSocketServiceInstance.currentCollection = selectedCollection;
        }
      } catch (error) {
        console.error("Error al reiniciar el chat:", error);
      }
    } else {
    }
  };
  useEffect(() => {
    if (configContext && !isLoadingConfig) {
      if (username === "admin" && config.DEFAULT_COLLECTION) {
        const defaultCollection = config.DEFAULT_COLLECTION;
        setCollections([defaultCollection]);
        setSelectedCollection(defaultCollection);

        if (webSocketServiceInstance) {
          webSocketServiceInstance.updateContext(
            defaultCollection,
            currentTopic ? currentTopic.id : ""
          );
        }

        setHasAccessError(false);
      }
    }
  }, [configContext, isLoadingConfig, username]);
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      if (!expirationDate) return;
      const now = new Date();
      const timeLeft = expirationDate.getTime() - now.getTime();

      if (timeLeft <= 0) {
        try {
          const data = await refreshToken();
          setIsLoggedIn(true);
          setIsGuest(false);
          setExpirationDate(new Date(Cookies.get("expirationDate")));
        } catch (error) {
          console.error("Error al refrescar el token:", error);
          Cookies.remove("accessToken");
          Cookies.remove("idToken");
          Cookies.remove("expirationDate");
          setIsLoggedIn(false);
          setIsGuest(true);
        }
      } else {
        const timer = setTimeout(checkAndRefreshToken, timeLeft - 60000);
        return () => clearTimeout(timer);
      }
    };

    checkAndRefreshToken();
  }, [expirationDate]);

  useEffect(() => {
    setIsLoggedIn(!!Cookies.get("accessToken"));
    setIsGuest(!Cookies.get("accessToken"));
  }, [Cookies.get("accessToken")]);

  const deleteTopicFromList = async (topic) => {
    if (hasAccessError) {
      return;
    }
    setIsLoadingChat(true);
    try {
      await deleteTopic(topic);
      await loadTopics();

      if (currentTopic && currentTopic.id === topic) {
        resetChat();
      }
    } catch (error) {
      console.error("Error al eliminar el tópico:", error);
      setLastError("Error al eliminar el tópico. Por favor, intenta nuevamente.");
    } finally {
      setIsLoadingChat(false);
    }
  };
  return (
    <ContextApp.Provider
      value={{
        showSlide,
        setShowSlide,
        chatValue,
        isInitialChat,
        setIsInitialChat,
        setChatValue,
        handleSend,
        message,
        cleanupAfterLogout,
        handleKeyPress,
        isWaitingForResponse,
        loadChatHistoryForTopic,
        currentTopic,
        topics,
        deleteTopicFromList,
        loadTopics,
        reloadTopicsTrigger,
        setReloadTopicsTrigger,
        resetChat,
        isLoggedIn,
        setIsLoggedIn,
        isGuest,
        setIsGuest,
        isAwaitingNewResponse,
        setIsAwaitingNewResponse,
        username,
        userRole,
        setUsername,
        collections,
        selectedCollection,
        setSelectedCollection,
        isCollectionSelectorOpen,
        setIsCollectionSelectorOpen,
        loadCollections,
        loadDocumentsForCollection,
        resetWebSocketConnection,
        isLoadingChat,
        setIsLoadingChat,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        isChatOpened,
        setIsChatOpened,
        currentCollectionDocuments,
        lastError,
        setLastError,
        hasAccessError,
        setHasAccessError,
        webSocketServiceInstance,
        updateCollectionContext,
      }}
    >
      {children}
    </ContextApp.Provider>
  );
};
export default AppContext;